// A request that must never leave the browser: the session was cleared - logout, or
// a session swap - between the caller deciding to load and axios dispatching.
//
// Every `*WithAuth` helper in repository.js builds its header as
// `'Bearer ' + (sessionStorage.getItem('token') || '')`, so once the token is gone
// the request still goes out, just unauthenticated. The gateway answers
// 401 "invalid signature", and the caller's catch reports that as a load failure -
// which lands as a toast on the login screen, long after the route moved on
// (QGEN-5569). Reject before dispatch instead, with a marker the caller can
// recognise so it can stay quiet about a load nobody is waiting for any more.

const SESSION_ENDED = 'ERR_SESSION_ENDED';

export interface SessionEndedError extends Error {
	code: string;
	// Carried deliberately: axios still runs the response error handlers for a
	// request rejected inside a request interceptor, and interceptors/buffer.js
	// keys its dedupe bookkeeping off `error.config`.
	config: unknown;
}

export const sessionEndedError = (config: unknown): SessionEndedError =>
	Object.assign(new Error('Request not sent: no active session'), {
		code: SESSION_ENDED,
		config,
	});

export const isSessionEndedError = (error: unknown): boolean =>
	Boolean(error) && (error as { code?: unknown }).code === SESSION_ENDED;

// By the time request interceptors run, axios has already turned `config.headers`
// into an AxiosHeaders instance; unit tests hand over a plain object. Read both.
const readAuthorization = (headers: unknown): string => {
	if (!headers || typeof headers !== 'object') {
		return '';
	}

	const bag = headers as { get?: (name: string) => unknown } & Record<
		string,
		unknown
	>;

	if (typeof bag.get === 'function') {
		return String(bag.get('Authorization') ?? '');
	}

	const key = Object.keys(bag).find(
		(name) => name.toLowerCase() === 'authorization'
	);
	return key ? String(bag[key] ?? '') : '';
};

// True only for a request that asks to be authenticated and has nothing to
// authenticate with. Public endpoints (login, password reset, the marketing pages)
// send no Authorization header at all, so they are never matched; neither is a
// non-bearer scheme.
export const isMissingBearerToken = (config: unknown): boolean => {
	const header = readAuthorization(
		(config as { headers?: unknown } | null)?.headers
	);
	if (!header) {
		return false;
	}

	const token = header.replace(/^Bearer\s*/i, '').trim();
	return token === '' || token === 'null' || token === 'undefined';
};
